// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { toast } from 'sonner';
import helpers from '@/utils/helpers';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import getConfig from '@staty/shared/utils/getConfig';
import {
  basketballStatMetadata,
  STAT_FIELDS_BY_SPORT,
  DEFAULT_GAME_STATS_BY_SPORT,
} from '@staty/shared/schemas/stat';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui/table';
import { Camera, Loader2, Plus, Trash2 } from 'lucide-react';
import ConfidenceBadge from './ConfidenceBadge';

import useGameStore from '@/store/gameStore';
import usePlayerStore from '@/store/playerStore';
import useStatStore from '@/store/statStore';
import useScoresheetStore from '@/store/scoresheetStore';

const STAT_FIELDS = STAT_FIELDS_BY_SPORT.basketball;
const DEFAULT_GAME_STATS = DEFAULT_GAME_STATS_BY_SPORT.basketball;

function resolveStatCols(division, org) {
  const sport = org?.sports?.[0] || division?.league?.sports?.[0] || 'basketball';
  const cfg = getConfig({
    division: division?.config || {},
    season: division?.league?.config || {},
    tournament: division?.league?.tournament?.config || {},
  });
  const resolved = _.get(cfg, ['gameStats', sport]) || org?.gameStats;
  return resolved && resolved.length
    ? resolved.filter((f) => STAT_FIELDS.includes(f))
    : DEFAULT_GAME_STATS;
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');

// Match a parsed row to an existing roster player: jersey number first, then name.
function matchPlayer(parsed, roster) {
  if (parsed.number != null && parsed.number !== '') {
    const byNum = roster.find((p) => String(p.number) === String(parsed.number));
    if (byNum) return byNum;
  }
  const pn = norm(parsed.name);
  if (pn) {
    const byName = roster.find((p) => norm(p.name) === pn)
      || roster.find((p) => norm(p.name).includes(pn) || pn.includes(norm(p.name)));
    if (byName) return byName;
  }
  return null;
}

const emptyPanel = () => ({ phase: 'idle', teamScore: '', rows: [], error: '' });

export default function ScoresheetScanBasketball({
  game, division, org, open, onOpenChange, onSubmitted,
}) {
  const { getToken } = useAuth();
  // Fall back to the org prop for legacy games missing `organization` (matches
  // the Pickleball dialog's pattern) — otherwise the photo upload posts to
  // /scoresheets/null/upload.
  const orgId = helpers.getObjId(game.organization) || helpers.getObjId(org);
  const leagueId = helpers.getObjId(game.league);
  const divisionId = helpers.getObjId(game.division);
  const homeId = helpers.getObjId(game.homeTeam);
  const awayId = helpers.getObjId(game.awayTeam);

  const { players, getPlayersByGameId } = usePlayerStore((s) => ({
    players: s.players, getPlayersByGameId: s.getPlayersByGameId,
  }));
  const updateGame = useGameStore((s) => s.updateGame(getToken));
  const bulkUpsertStats = useStatStore((s) => s.bulkUpsertStats(getToken));
  const { uploadScoresheet, parseScoresheet, pollScoresheetJob, createPlayer } = useScoresheetStore((s) => ({
    uploadScoresheet: s.uploadScoresheet,
    parseScoresheet: s.parseScoresheet,
    pollScoresheetJob: s.pollScoresheetJob,
    createPlayer: s.createPlayer,
  }));

  const statCols = useMemo(() => resolveStatCols(division, org), [division, org]);

  useEffect(() => {
    if (open && game?._id) getPlayersByGameId(game._id);
  }, [open, game?._id]);

  const rosterBySide = useMemo(() => {
    const home = players.filter((p) => helpers.getObjId(p.team) === homeId);
    const away = players.filter((p) => helpers.getObjId(p.team) === awayId);
    return { home, away };
  }, [players, homeId, awayId]);

  const [panels, setPanels] = useState({ home: emptyPanel(), away: emptyPanel() });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setPanels({ home: emptyPanel(), away: emptyPanel() });
  }, [open]);

  const setPanel = (side, patch) =>
    setPanels((prev) => ({ ...prev, [side]: { ...prev[side], ...(typeof patch === 'function' ? patch(prev[side]) : patch) } }));

  const sideName = (side) => {
    const ref = side === 'home' ? game.homeTeam : game.awayTeam;
    return ref && typeof ref === 'object' ? getTeamDisplayName(ref) : side === 'home' ? 'Home' : 'Away';
  };

  const buildRows = (parsed, roster) =>
    (parsed.players || []).map((p, i) => {
      const match = matchPlayer(p, roster);
      const stats = {};
      statCols.forEach((k) => { stats[k] = p.stats?.[k] ?? null; });
      return {
        key: `row-${i}-${Math.floor(performance.now())}`,
        matchedPlayerId: match?._id || '__new__',
        name: p.name || match?.name || '',
        number: p.number ?? match?.number ?? '',
        stats,
        confidence: p.confidence || 'medium',
      };
    });

  const onPickPhoto = async (side, file) => {
    if (!file) return;
    const roster = rosterBySide[side];
    setPanel(side, { phase: 'uploading', error: '' });
    try {
      const token = await getToken();
      const imageUrl = await uploadScoresheet(file, orgId, token);
      setPanel(side, { phase: 'analyzing' });
      const { jobId } = await parseScoresheet({
        sport: 'basketball',
        imageUrl,
        divisionId,
        gameId: game._id,
        teamHint: `This scoresheet is for the ${side} team, "${sideName(side)}".`,
        gameStatKeys: statCols,
        roster: roster.map((p) => ({ name: p.name, number: String(p.number ?? '') })),
      }, token);
      const result = await pollScoresheetJob(jobId, token);
      setPanel(side, {
        phase: 'done',
        rows: buildRows(result, roster),
        teamScore: result.teamScore != null ? String(result.teamScore) : '',
      });
    } catch (e) {
      console.error('scoresheet scan failed', e);
      setPanel(side, { phase: 'error', error: e?.message || 'Scan failed' });
      toast.error(e?.message || 'Scoresheet scan failed');
    }
  };

  const updateRow = (side, key, patch) =>
    setPanel(side, (p) => ({ rows: p.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const updateRowStat = (side, key, statKey, value) =>
    setPanel(side, (p) => ({
      rows: p.rows.map((r) => (r.key === key ? { ...r, stats: { ...r.stats, [statKey]: value === '' ? null : value } } : r)),
    }));

  const addRow = (side) =>
    setPanel(side, (p) => ({
      rows: [...p.rows, {
        key: `row-new-${p.rows.length}-${Math.floor(performance.now())}`,
        matchedPlayerId: '__new__', name: '', number: '',
        stats: Object.fromEntries(statCols.map((k) => [k, null])),
        confidence: 'high',
      }],
    }));

  const removeRow = (side, key) =>
    setPanel(side, (p) => ({ rows: p.rows.filter((r) => r.key !== key) }));

  const buildStatRows = async (side, token) => {
    const teamId = side === 'home' ? homeId : awayId;
    const vsTeam = side === 'home' ? awayId : homeId;
    const roster = rosterBySide[side];
    const out = [];
    for (const row of panels[side].rows) {
      if (!row.name && !row.number) continue;
      let playerId = row.matchedPlayerId;
      if (!playerId || playerId === '__new__') {
        const created = await createPlayer({
          name: row.name || `#${row.number}`,
          number: String(row.number ?? ''),
          team: teamId,
          organization: orgId,
          league: leagueId,
          division: divisionId,
        }, token);
        if (!created?._id) continue;
        playerId = created._id;
        // keep roster fresh so a re-run matches
        roster.push(created);
      }
      const doc = {
        player: playerId, team: teamId, vsTeam, game: game._id,
        league: leagueId, division: divisionId, organization: orgId,
      };
      statCols.forEach((k) => {
        const v = row.stats[k];
        doc[k] = v == null || v === '' ? -1 : Number(v);
      });
      out.push(doc);
    }
    return out;
  };

  const canSubmit = panels.home.phase === 'done' || panels.away.phase === 'done';

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const homeRows = await buildStatRows('home', token);
      const awayRows = await buildStatRows('away', token);
      await updateGame({
        ...game,
        homeScore: panels.home.teamScore === '' ? game.homeScore : Number(panels.home.teamScore),
        awayScore: panels.away.teamScore === '' ? game.awayScore : Number(panels.away.teamScore),
      });
      const statRows = [...homeRows, ...awayRows];
      if (statRows.length) await bulkUpsertStats(statRows);
      toast.success('Results saved');
      onSubmitted && onSubmitted();
      onOpenChange(false);
    } catch (e) {
      console.error('submit failed', e);
      toast.error('Failed to save results');
    } finally {
      setSubmitting(false);
    }
  };

  const renderPanel = (side) => {
    const panel = panels[side];
    const busy = panel.phase === 'uploading' || panel.phase === 'analyzing';
    return (
      <div className="border rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold">{sideName(side)}</div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Team score</Label>
            <Input
              type="text" inputMode="numeric" className="h-8 w-20"
              value={panel.teamScore}
              onChange={(e) => setPanel(side, { teamScore: e.target.value.replace(/[^0-9]/g, '') })}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="inline-flex">
            <input
              type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => onPickPhoto(side, e.target.files?.[0])}
            />
            <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md border cursor-pointer text-sm hover:bg-accent">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {panel.phase === 'uploading' ? 'Uploading…'
                : panel.phase === 'analyzing' ? 'Reading scoresheet…'
                : panel.phase === 'done' ? 'Retake photo' : 'Take / upload photo'}
            </span>
          </label>
          {panel.phase === 'done' && (
            <Button type="button" variant="ghost" size="sm" onClick={() => addRow(side)}>
              <Plus className="h-4 w-4 mr-1" /> Add player
            </Button>
          )}
        </div>

        {panel.error && <div className="text-sm text-destructive">{panel.error}</div>}

        {panel.phase === 'done' && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-44">Player</TableHead>
                  <TableHead className="w-16">No.</TableHead>
                  <TableHead className="w-40">Match</TableHead>
                  {statCols.map((c) => (
                    <TableHead key={c} className="text-center w-20">
                      {basketballStatMetadata[c]?.headerName || c}
                    </TableHead>
                  ))}
                  <TableHead className="w-28">Confidence</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {panel.rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="p-1">
                      <Input className="h-8" value={row.name}
                        onChange={(e) => updateRow(side, row.key, { name: e.target.value })} />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input className="h-8 w-14" value={row.number ?? ''}
                        onChange={(e) => updateRow(side, row.key, { number: e.target.value })} />
                    </TableCell>
                    <TableCell className="p-1">
                      <select
                        className="h-8 w-full rounded-md border bg-background text-sm px-1"
                        value={row.matchedPlayerId}
                        onChange={(e) => updateRow(side, row.key, { matchedPlayerId: e.target.value })}
                      >
                        <option value="__new__">➕ New player</option>
                        {rosterBySide[side].map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.number ? `#${p.number} ` : ''}{p.name}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    {statCols.map((c) => (
                      <TableCell key={c} className="p-1 text-center">
                        <Input
                          type="text" inputMode="numeric"
                          className="h-8 w-16 mx-auto text-center"
                          value={row.stats[c] == null ? '' : String(row.stats[c])}
                          onChange={(e) => updateRowStat(side, row.key, c, e.target.value.replace(/[^0-9]/g, ''))}
                        />
                      </TableCell>
                    ))}
                    <TableCell><ConfidenceBadge level={row.confidence} /></TableCell>
                    <TableCell className="p-1">
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => removeRow(side, row.key)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    );
  };

  if (!homeId || !awayId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Scan scoresheets</DialogTitle>
            <DialogDescription>
              Both teams must be assigned to this game before scanning scoresheets.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scan scoresheets: {sideName('home')} vs {sideName('away')}</DialogTitle>
          <DialogDescription>
            Take a photo of each team&apos;s scoresheet. Review the AI-filled stats and confidence,
            edit as needed, then submit. Two sheets, one per team.{' '}
            <Link
              to={`/admin/game/${game._id}/stats`}
              className="text-primary underline underline-offset-2 hover:no-underline"
            >
              No scoresheet? Enter stats manually
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {renderPanel('home')}
          {renderPanel('away')}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Submit results
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
